'use client'

import React, { useCallback, useMemo, useState } from 'react'
import { useTranslations } from 'next-intl'
import { ResourceItem, ResourceCategory } from '../../../types/ResourceHub'
import { ColorSchemeTypes } from '../../../types/ColorScheme'
import Icon from '../../atoms/Icon/Icon.component'
import Button from '../../atoms/Button/Button.component'
import Spinner from '../../atoms/Spinner/Spinner.component'
import ModalAuth from '../../molecules/Modal/ModalAuth/ModalAuth.component'
import { useModal } from '../../../contexts/Modal.context'
import * as S from './ResourceHub.styles'

type ResourceHubCardProps = {
  item: ResourceItem
  colorScheme: ColorSchemeTypes
}

const CATEGORY_ICONS: Record<ResourceCategory, string> = {
  safetyDatasheet: 'safetyDatasheet',
  technicalSheet: 'technicalSheet',
  caseStudy: 'caseStudy',
  whitepaper: 'whitepaper',
  certification: 'certification',
}

function formatFileSize(sizeInKB: number): string {
  if (sizeInKB >= 1024) {
    return `${(sizeInKB / 1024).toLocaleString('it-IT', { maximumFractionDigits: 1 })} MB`
  }
  return `${sizeInKB.toLocaleString('it-IT', { maximumFractionDigits: 1 })} KB`
}

function ResourceHubCard({ item, colorScheme }: ResourceHubCardProps) {
  const t = useTranslations('resourceHub')

  const { showModal } = useModal()
  const isSafetyDatasheet = item.resourceCategory === 'safetyDatasheet'
  const attachedFile = item.attachedDocument?.file ?? null
  const hasAttachedFile = !!attachedFile
  const hasLink = !!item.link?.href
  const hasCaseStudy = !!item.caseStudy

  const [isDownloading, setIsDownloading] = useState(false)

  const showDownloadError = useCallback((message?: string) => {
    showModal(ModalAuth as React.ComponentType, {
      title: t('card.downloadErrorTitle'),
      description: message ?? t('card.downloadError'),
      foreground: true,
    })
  }, [showModal, t])

  const handleDatasheetDownload = useCallback(async () => {
    setIsDownloading(true)
    try {
      const url = `/api/resources/download/${item.id}`
      const response = await fetch(url)

      if (!response.ok) {
        const error = await response.json().catch(() => null)
        showDownloadError(error?.error)
        return
      }

      // Chrome's PDF viewer saves the file by requesting the document URL again.
      // A blob: URL is revoked immediately and that second request fails with a network error.
      await response.arrayBuffer()
      window.open(url, '_blank')
    } catch {
      showDownloadError()
    } finally {
      setIsDownloading(false)
    }
  }, [item.id, showDownloadError])

  const handleDownload = useCallback(async () => {
    if (!attachedFile) return

    setIsDownloading(true)
    try {
      const response = await fetch(attachedFile.url)
      const blob = await response.blob()
      const blobUrl = URL.createObjectURL(blob)
      const anchor = document.createElement('a')
      anchor.href = blobUrl
      anchor.download = attachedFile.name
      document.body.appendChild(anchor)
      anchor.click()
      document.body.removeChild(anchor)
      URL.revokeObjectURL(blobUrl)
    } catch (error) {
      window.open(attachedFile.url, '_blank')
    } finally {
      setIsDownloading(false)
    }
  }, [attachedFile])

  const fileInfoLabel = useMemo(() => {
    if (!attachedFile) return null
    
    return attachedFile.ext?.replace('.', '').toUpperCase()
  }, [attachedFile])

  return (
    <S.ResourceHubCard>
      <S.ResourceHubCardHeader>
        {item.resourceCategory && (
          <S.ResourceHubCardCategory>
            <Icon name={CATEGORY_ICONS[item.resourceCategory]} aria-hidden />
            <span>
              {t(`categories.${item.resourceCategory}`)}
            </span>
          </S.ResourceHubCardCategory>
        )}
        {fileInfoLabel && (
          <S.ResourceHubCardFileInfo>{fileInfoLabel}</S.ResourceHubCardFileInfo>
        )}
      </S.ResourceHubCardHeader>

      {item.title && (
        <S.ResourceHubCardTitle>{item.title}</S.ResourceHubCardTitle>
      )}

      {item.description && (
        <S.ResourceHubCardDescription
          dangerouslySetInnerHTML={{ __html: item.description }}
        />
      )}

      <S.ResourceHubCardFooter>
        {isSafetyDatasheet ? (
          <Button
            variant="primary"
            iconEnd={isDownloading ? undefined : 'download'}
            label={t('card.downloadDatasheet')}
            handleClick={handleDatasheetDownload}
            inheritedColorScheme={colorScheme}
            disabled={isDownloading}
          >
            {isDownloading ? <Spinner inline size={18} /> : null}
          </Button>
        ) : hasAttachedFile ? (
          <Button
            variant="primary"
            iconEnd={isDownloading ? undefined : 'download'}
            label={t('card.download')}
            handleClick={handleDownload}
            inheritedColorScheme={colorScheme}
            disabled={isDownloading}
          >
            {isDownloading ? <Spinner inline size={18} /> : null}
          </Button>
        ) : hasLink ? (
          <Button
            variant="primary"
            iconEnd="arrow-up-right"
            label={t('card.visit')}
            href={item.link!.href!}
            target={'_blank'}
            inheritedColorScheme={colorScheme}
          />
        ) : hasCaseStudy ? (
          <Button
            variant="primary"
            iconEnd="arrow-up-right"
            label={t('card.discoverMore')}
            href={`/${item.caseStudy!.locale}/use-cases/${item.caseStudy!.slug}`}
            target={'_blank'}
            inheritedColorScheme={colorScheme}
          />
        ) : null}
      </S.ResourceHubCardFooter>
    </S.ResourceHubCard>
  )
}

export default ResourceHubCard
